import { usePaginatedSiteAssets } from "../hooks/usePaginatedSiteAssets"
import AssetsTableAdvanced from "./PaginatedTable"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import type { ExpandableRowConfig } from "@hydrologiq/shared/components/PaginatedTable"

interface AssetsTableProps {
  customerId?: string
  siteId?: string
  external?: boolean
  onNewLocation?: (location: gqlTypes.Location) => void
  headerActions?: React.ReactNode
  expandableRows?: ExpandableRowConfig<gqlTypes.Asset>
}

function AssetsTable({ customerId, siteId, external = false, onNewLocation, headerActions, expandableRows }: AssetsTableProps) {
  const {
    data: assets,
    pagination: assetsPagination,
    filterOptions: assetsFilterOptions,
    loading: assetsLoading,
    error: assetsError,
    filters: assetsFilters,
    sort: assetsSort,
    fetchPage: fetchAssetsPage,
    setFilters: setAssetsFilters,
    setSort: setAssetsSort,
    clearFilters: clearAssetsFilters,
    refetch: refetchAssets,
  } = usePaginatedSiteAssets(customerId, siteId)
  
  // Only show customer column when not filtering by customer/site (i.e., main assets listing)
  const showCustomerColumn = !customerId && !siteId
  
  return (
    <AssetsTableAdvanced
      assets={assets}
      loading={assetsLoading}
      error={assetsError}
      onRetry={refetchAssets}
      serverPagination={{
        pagination: {
          page: assetsPagination?.page || 1,
          per_page: assetsPagination?.per_page || 10,
          total_count: assetsPagination?.total_count || 0,
          total_pages: assetsPagination?.total_pages || 0,
          has_next: assetsPagination?.has_next || false,
          has_prev: assetsPagination?.has_prev || false,
        },
        onPageChange: (page: number) => fetchAssetsPage(page),
        onPageSizeChange: (pageSize: number) => fetchAssetsPage(undefined, pageSize),
      }}
      serverFiltering={{
        filters: assetsFilters || {},
        sort: assetsSort,
        onFiltersChange: setAssetsFilters,
        onSortChange: setAssetsSort,
        onClearAll: clearAssetsFilters,
      }}
      filterOptions={assetsFilterOptions}
      openExternal={external}
      onNewLocation={onNewLocation}
      headerActions={headerActions}
      showCustomerColumn={showCustomerColumn}
      expandableRows={expandableRows}
    />
  )
}

export default AssetsTable
